import type { IJuryDuties } from '../../../../redux/jury/types';
import type { Juror, PendingPagination } from '../../../../redux/contest/types';
import type { IUserSummary } from '../../../../types/global/helper';

const defaultPendingPagination = (): PendingPagination => ({
    currentPage: 0,
    pageSize: 0,
    totalPending: 0,
    hasMore: false,
    remaining: 0,
});

function resolveJurorUser(duty: IJuryDuties, existing?: Juror): IUserSummary | undefined {
    if (typeof duty.jury === 'object' && duty.jury !== null && '_id' in duty.jury) {
        return duty.jury as IUserSummary;
    }
    return existing?.juror;
}

/** Convert a jury-duty record into manage-page `Juror` shape, preserving stats when available. */
export function juryDutyToJuror(duty: IJuryDuties, existing?: Juror): Juror {
    const jurorUser = resolveJurorUser(duty, existing);
    const assignedFromDuty = (duty.totalCompleted ?? 0) + (duty.totalRemaining ?? 0);

    return {
        _id: duty._id,
        accepted_invite: existing?.accepted_invite ?? duty.accepted_invite ?? false,
        invite_sent: existing?.invite_sent ?? duty.invite_sent ?? true,
        judged_submissions: existing?.judged_submissions ?? duty.totalCompleted ?? 0,
        judgment_percentage: existing?.judgment_percentage ?? duty.percentageScored ?? 0,
        juror: jurorUser ?? existing?.juror ?? ({} as IUserSummary),
        pending_pagination: existing?.pending_pagination ?? defaultPendingPagination(),
        pending_submissions: existing?.pending_submissions ?? [],
        total_assigned: existing?.total_assigned ?? assignedFromDuty,
        jury_settings: duty.jury_settings ?? existing?.jury_settings!,
    };
}

/**
 * Merge juror lists by assignment `_id` so freshly invited jurors are not dropped when
 * a stale manage-campaign refetch returns an older panel snapshot.
 */
export function mergeContestJurors(existing: Juror[], incoming: Juror[]): Juror[] {
    const byId = new Map<string, Juror>();

    for (const juror of existing) {
        if (juror?._id) {
            byId.set(juror._id, juror);
        }
    }

    for (const juror of incoming) {
        if (!juror?._id) continue;
        const prev = byId.get(juror._id);
        if (prev) {
            byId.set(juror._id, {
                ...prev,
                ...juror,
                juror: juror.juror && typeof juror.juror === 'object' && juror.juror._id ? juror.juror : prev.juror,
                pending_submissions: juror.pending_submissions?.length
                    ? juror.pending_submissions
                    : prev.pending_submissions,
                pending_pagination: juror.pending_pagination ?? prev.pending_pagination,
            });
        } else {
            byId.set(juror._id, juror);
        }
    }

    return Array.from(byId.values());
}

export function juryDutiesToJurors(duties: IJuryDuties[], existing: Juror[] = []): Juror[] {
    const existingById = new Map(existing.map(j => [j._id, j]));
    return duties.map(duty => juryDutyToJuror(duty, existingById.get(duty._id)));
}
